import React from "react";
import { PlateTypeCaptureProfile, UltravioletCaptureProperty } from "../../../Model/CaptureProfile";
import EOpticAndCameraFeature from "../../../Model/EOpticAndCameraFeature";
import { LightPathCapability } from "../../../Model/ImagerCapabilities";
import DefaultImagingSettingBinning from "./DefaultImagingSettingBinning";
import DefaultImagingSettingTextBox from "./DefaultImagingSettingTextBox";
import Row from "./Row";
import "./VisibleOpticAndExposure.css";

type Props = {
  lightpathCapabilities: LightPathCapability;
  captureProfile: PlateTypeCaptureProfile;
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
  style?: React.CSSProperties;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
};

export default function UltravioletOpticAndExposureOptions({
  style = {},
  lightpathCapabilities,
  captureProfile,
  setCaptureProfile,
  onErrorOccur,
  onErrorCleared,
}: Props) {
  const ultravioletCaptureProperty = captureProfile.ultraviolet;
  if (ultravioletCaptureProperty == null) {
    throw new Error("Ultraviolet capture property is null");
  }

  const onValueChanged = (ultravioletCaptureProperty: UltravioletCaptureProperty) => {
    setCaptureProfile({ ...captureProfile, ultraviolet: ultravioletCaptureProperty });
  };

  const gamma = lightpathCapabilities.camera.gamma;
  const gain = lightpathCapabilities.camera.gain;
  const binning = lightpathCapabilities.camera.binning;
  const exposure = lightpathCapabilities.camera.exposure;

  let gammaView = null;
  if (gamma != null) {
    if (ultravioletCaptureProperty.gamma == null) {
      throw new Error("Capture profole gamma is null");
    }
    gammaView = (
      <DefaultImagingSettingTextBox
        value={ultravioletCaptureProperty.gamma}
        onChange={(name, value) => onValueChanged({ ...ultravioletCaptureProperty, gamma: value })}
        label="Gamma"
        name={EOpticAndCameraFeature.Gamma}
        unit=""
        min={gamma.minGamma}
        max={gamma.maxGamma}
        disabled={false}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let gainView = null;
  if (gain != null) {
    if (ultravioletCaptureProperty.gain == null) {
      throw new Error("Capture profile gain is null");
    }
    gainView = (
      <DefaultImagingSettingTextBox
        value={ultravioletCaptureProperty.gain}
        onChange={(name, value) => onValueChanged({ ...ultravioletCaptureProperty, gain: value })}
        label="Gain"
        name={EOpticAndCameraFeature.Gain}
        unit="db"
        min={gain.minGain}
        max={gain.maxGain}
        disabled={false}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let binningView = null;
  if (binning != null) {
    if (ultravioletCaptureProperty.binning == null) {
      throw new Error("Capture profile binning is null");
    }
    binningView = (
      <DefaultImagingSettingBinning
        binningDisplayValues={binning.binningDisplayValues}
        value={ultravioletCaptureProperty.binning}
        style={{
          marginLeft: "17px",
          marginBottom: "5px",
        }}
        onChange={(name, value) => onValueChanged({ ...ultravioletCaptureProperty, binning: value })}
        name="binning"
        disabled={false}
      />
    );
  }

  let exposureView = null;

  if (exposure != null) {
    if (ultravioletCaptureProperty.exposure == null) {
      throw new Error("Capture profile exposure is null");
    }

    exposureView = (
      <DefaultImagingSettingTextBox
        value={ultravioletCaptureProperty.exposure}
        onChange={(name, value) => {
          onValueChanged({
            ...ultravioletCaptureProperty,
            exposure: value,
          });
        }}
        label="Exposure"
        name="exposure"
        unit="ms"
        min={exposure.minExposure}
        max={exposure.maxExposure}
        isInteger={false}
        disabled={false}
        style={{
          gridArea: "exposure",
        }}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  return (
    <div
      className="visible-optic-exposure"
      style={{
        ...style,
      }}
    >
      <span className="title">Optic Options</span>
      {binningView}
      <Row>
        {gammaView}
        {gainView}
      </Row>
      <span className="title">Exposure Options</span>
      <Row>
        {exposureView}
        {null}
      </Row>
    </div>
  );
}
